'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthSession } from '@/app/components/auth/useAuthSession';
import type { Property, RatePlan } from '@/core/domain/entities';
import AvailabilityCalendar from './AvailabilityCalendar';
import { useRatePlanSelection } from './RatePlanProvider';
import { analytics } from '@/adapters/analytics';
import NoticeAtCollection from '@/app/components/legal/NoticeAtCollection';
import { strings } from '@/content/strings';
import { useAvailabilityContext } from './AvailabilityProvider';

type SheetView = 'main' | 'calendar' | 'guests' | 'rate' | 'notice';

interface MobileBookingSheetProps {
  property: Property;
  ratePlans?: RatePlan[];
  blocked?: { start: string; end: string; source: 'booking' | 'ical' | 'manual' }[];
  minNightOverrides?: { start: string; end: string; minNights: number }[];
}

export default function MobileBookingSheet({
  property,
  ratePlans = [],
  blocked = [],
  minNightOverrides = [],
}: MobileBookingSheetProps) {
  const availability = useAvailabilityContext();
  const blockedDates = availability?.blocked ?? blocked;
  const minNightOverridesState = availability?.minNightOverrides ?? minNightOverrides;
  const propertyStrings = strings.screens.property;
  const currencySymbol = strings.common.symbols.currency;
  const defaultAdults = Math.min(2, property.maxGuests);
  const [guestCounts, setGuestCounts] = useState({
    adults: defaultAdults,
    children: 0,
    infants: 0,
    pets: 0,
  });
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSheet, setActiveSheet] = useState<SheetView>('main');
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const { data: session } = useAuthSession();
  const lockedEmail = !!session?.user?.email;
  const totalGuests = guestCounts.adults + guestCounts.children + guestCounts.infants;
  const formattedTaxRate = Number.isFinite(property.taxRate)
    ? `${property.taxRate}`
    : '0';
  const taxRateLabel =
    (property.taxRate ?? 0) > 0
      ? `${propertyStrings.taxes} (${formattedTaxRate}%)`
      : propertyStrings.taxes;
  const ratePlanSelection = useRatePlanSelection();
  const [localRatePlanId, setLocalRatePlanId] = useState(ratePlans[0]?.id ?? '');
  const selectedRatePlanId = ratePlanSelection?.selectedRatePlanId ?? localRatePlanId;
  const setSelectedRatePlanId = (nextId: string) => {
    ratePlanSelection?.setSelectedRatePlanId(nextId);
    setLocalRatePlanId(nextId);
  };

  const planFromQuote = quote?.ratePlan as RatePlan | undefined;
  const activePlan =
    planFromQuote && planFromQuote.id === selectedRatePlanId
      ? planFromQuote
      : ratePlans.find((p) => p.id === selectedRatePlanId);
  const activeLeadDays = activePlan?.minLeadDays ?? 0;

  const paymentSummary = activePlan
    ? activePlan.paymentRule.type === 'full-prepay'
      ? propertyStrings.paymentSummaryFullPrepay
      : activePlan.paymentRule.type === 'pay-at-checkin'
        ? propertyStrings.paymentSummaryPayAtCheckIn
        : propertyStrings.paymentSummaryDeposit(
            Math.round(activePlan.paymentRule.depositPercent * 100),
            activePlan.paymentRule.remainderDaysBeforeCheckIn
          )
    : propertyStrings.paymentSummaryDefault;

  const cancellationSummary = activePlan
    ? activePlan.refundLadder.length === 0
      ? propertyStrings.cancellationSummaryNonRefundable
      : (() => {
          const top = [...activePlan.refundLadder].sort(
            (a, b) => b.cutoffDaysBeforeCheckIn - a.cutoffDaysBeforeCheckIn
          )[0];
          const pct = Math.round(top.refundPercent * 100);
          return pct >= 100
            ? propertyStrings.cancellationSummaryFreeCancel(top.cutoffDaysBeforeCheckIn)
            : propertyStrings.cancellationSummaryPercent(pct, top.cutoffDaysBeforeCheckIn);
        })()
    : propertyStrings.cancellationSummaryDefault;

  const serviceFeeRate = property.serviceFeeRate ?? 0;
  const baseNightlyRateWithFee = Math.round(property.baseNightlyRate * (1 + serviceFeeRate / 100));
  const quotePricing = quote?.pricing;
  const guestSubtotal = quotePricing
    ? quotePricing.subtotal + (quotePricing.serviceFee ?? 0)
    : null;
  const discountAmount = quotePricing?.discount ?? 0;
  const hasDiscount = discountAmount > 0;
  const breakdownNightlyTotal = quotePricing
    ? (guestSubtotal ?? 0) + (hasDiscount ? discountAmount : 0)
    : null;
  const breakdownNightlyRate =
    quotePricing && quotePricing.nights > 0
      ? Math.round((breakdownNightlyTotal ?? 0) / quotePricing.nights)
      : baseNightlyRateWithFee;
  const displayedNightlyRate =
    quotePricing && quotePricing.nights > 0
      ? Math.round((guestSubtotal ?? 0) / quotePricing.nights)
      : baseNightlyRateWithFee;

  useEffect(() => {
    if (!session?.user?.email) return;
    setEmail(session.user.email);
  }, [session?.user?.email]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    const updateHeight = () => {
      const height = barRef.current?.offsetHeight ?? 0;
      if (height > 0) {
        root.style.setProperty('--mobile-booking-bar-height', `${height}px`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (barRef.current) observer.observe(barRef.current);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
      root.style.removeProperty('--mobile-booking-bar-height');
    };
  }, [mounted]);

  useEffect(() => {
    const body = document.body;
    if (!open) {
      delete body.dataset.mobileBookingOpen;
      body.style.overflow = '';
      return;
    }
    body.dataset.mobileBookingOpen = 'true';
    body.style.overflow = 'hidden';
    return () => {
      delete body.dataset.mobileBookingOpen;
      body.style.overflow = '';
    };
  }, [open]);

  const calculateQuote = async () => {
    if (!checkIn || !checkOut) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || undefined,
          checkIn,
          checkOut,
          numberOfGuests: totalGuests,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
      } else {
        setError(data.errors?.join(', ') || data.error);
        setQuote(null);
      }
    } catch (err) {
      setError(propertyStrings.calculateQuoteError);
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const quoteTimerRef = useRef<number | null>(null);
  const scheduleQuote = (delay = 250) => {
    if (!checkIn || !checkOut) return;
    if (!isMobile || !open) return;
    if (quoteTimerRef.current) window.clearTimeout(quoteTimerRef.current);
    quoteTimerRef.current = window.setTimeout(() => {
      void calculateQuote();
    }, delay);
  };

  useEffect(() => {
    if (!defaultsLoaded && !checkIn && !checkOut) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      if (activeLeadDays > 0) {
        start.setDate(start.getDate() + activeLeadDays);
      }
      const end = new Date(start);
      end.setDate(end.getDate() + 5);
      setCheckIn(dateKeyLocal(start));
      setCheckOut(dateKeyLocal(end));
      setDefaultsLoaded(true);
      scheduleQuote(200);
      return;
    }
    setDefaultsLoaded(true);
  }, [defaultsLoaded, checkIn, checkOut, activeLeadDays]);

  useEffect(() => {
    if (checkIn && checkOut) {
      scheduleQuote();
    }
  }, [totalGuests, checkIn, checkOut, selectedRatePlanId, isMobile, open]);

  const updateGuestCount = (key: 'adults' | 'children' | 'infants' | 'pets', delta: number) => {
    setGuestCounts((prev) => {
      const min = key === 'adults' ? 1 : 0;
      const nextValue = prev[key] + delta;
      if (nextValue < min) return prev;

      if (key !== 'pets') {
        const nextTotal = prev.adults + prev.children + prev.infants + delta;
        if (nextTotal > property.maxGuests) return prev;
      } else {
        const maxPets = property.maxPets ?? 0;
        if (maxPets === 0) return prev;
        if (nextValue > maxPets) return prev;
      }

      return { ...prev, [key]: nextValue };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || undefined,
          guest: { firstName, lastName, email, phone },
          numberOfGuests: totalGuests,
          checkIn,
          checkOut,
          specialRequests,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        analytics.track('booking_request_submitted', {
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || 'default',
        });
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setSpecialRequests('');
      } else {
        setError(data.errors?.join(', ') || data.error);
      }
    } catch (err) {
      setError(propertyStrings.submitBookingError);
    } finally {
      setLoading(false);
    }
  };

  const summaryDate = checkIn && checkOut
    ? `${checkIn} → ${checkOut}`
    : propertyStrings.datePlaceholder;
  const summaryGuests = `${totalGuests}`;
  const summaryPrice = (displayedNightlyRate / 100).toFixed(2);
  const disclosure = strings.common.symbols.disclosure;

  const onClose = () => {
    setOpen(false);
    setActiveSheet('main');
  };

  const openSheet = (view: SheetView) => {
    setActiveSheet(view);
    setOpen(true);
  };

  const sheetTitle = useMemo(() => {
    if (activeSheet === 'calendar') return propertyStrings.mobileSelectDates;
    if (activeSheet === 'guests') return propertyStrings.mobileSelectGuests;
    if (activeSheet === 'rate') return propertyStrings.mobileRateDetails;
    if (activeSheet === 'notice') return strings.common.notice.title;
    return propertyStrings.mobileBookTitle;
  }, [activeSheet, propertyStrings]);

  const totalLabel = quotePricing
    ? `${currencySymbol}${(quotePricing.total / 100).toFixed(2)}`
    : propertyStrings.datePlaceholder;

  if (!isMobile) return null;

  const portalTarget = mounted ? document.body : null;
  const content = (
    <>
      <div
        ref={barRef}
        className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur px-4 py-3"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">{propertyStrings.mobilePricePerNight}</div>
            <div className="text-base font-semibold text-foreground">${summaryPrice}</div>
          </div>
          <button
            type="button"
            onClick={() => openSheet('main')}
            className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
          >
            {propertyStrings.mobileBookNow}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />

          <div className="absolute bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-foreground">{propertyStrings.mobileBookTitle}</div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-border bg-ghost px-3 py-1 text-xs font-semibold text-foreground"
              >
                {propertyStrings.mobileClose}
              </button>
            </div>

            <div className={`mt-4 space-y-5 ${activeSheet !== 'main' ? 'pointer-events-none opacity-40' : ''}`}>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setActiveSheet('calendar')}
                    className="w-full rounded-2xl border border-border bg-ghost px-4 py-3 text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">{propertyStrings.selectDatesTitle}</div>
                        <div className="text-sm font-semibold text-foreground">{summaryDate}</div>
                      </div>
                      <span className="text-sm text-muted-foreground">{disclosure}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveSheet('guests')}
                    className="w-full rounded-2xl border border-border bg-ghost px-4 py-3 text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">{propertyStrings.guestsLabel}</div>
                        <div className="text-sm font-semibold text-foreground">{summaryGuests}</div>
                      </div>
                      <span className="text-sm text-muted-foreground">{disclosure}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveSheet('rate')}
                    className="w-full rounded-2xl border border-border bg-ghost px-4 py-3 text-left"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">{propertyStrings.mobileTotalLabel}</div>
                        <div className="text-sm font-semibold text-foreground">{totalLabel}</div>
                      </div>
                      <span className="text-sm text-muted-foreground">{disclosure}</span>
                    </div>
                  </button>
                </div>

                {success ? (
                  <div className="bg-accent/30 border border-border text-foreground rounded-lg p-4">
                    <h3 className="font-semibold mb-1">{propertyStrings.bookingRequestTitle}</h3>
                    <p className="text-sm">{propertyStrings.bookingRequestCopy}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-foreground">
                        {propertyStrings.guestInfoTitle}
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveSheet('notice')}
                        className="text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground"
                      >
                        {strings.common.notice.noticeLink}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        required
                        placeholder={propertyStrings.firstNamePlaceholder}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      />
                      <input
                        type="text"
                        required
                        placeholder={propertyStrings.lastNamePlaceholder}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      />
                    </div>
                    <input
                      type="email"
                      required
                      placeholder={propertyStrings.emailPlaceholder}
                      value={email}
                      onChange={(e) => {
                        if (!lockedEmail) setEmail(e.target.value);
                      }}
                      readOnly={lockedEmail}
                      aria-readonly={lockedEmail}
                      className={`w-full px-3 py-2 border border-border rounded-lg text-foreground ${
                        lockedEmail ? 'bg-ghost/70 text-muted-foreground' : 'bg-background'
                      }`}
                    />
                    <input
                      type="tel"
                      required
                      placeholder={propertyStrings.phonePlaceholder}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    />
                    <textarea
                      placeholder={propertyStrings.specialRequests}
                      value={specialRequests}
                      onChange={(e) => setSpecialRequests(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    />
                    {error && (
                      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-rose-700 text-sm">
                        {error}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading || !quote}
                      className={`w-full py-3 px-4 rounded-lg font-semibold text-primary-foreground transition-colors ${
                        loading || !quote
                          ? 'bg-muted cursor-not-allowed text-muted-foreground'
                          : 'bg-primary hover:opacity-90 active:opacity-100'
                      }`}
                    >
                      {loading ? propertyStrings.processing : propertyStrings.requestToBook}
                    </button>
                    <p className="text-xs text-muted-foreground text-center">
                      {propertyStrings.bookingHelper}
                    </p>
                  </form>
                )}
              </div>
          </div>

          {activeSheet === 'calendar' && (
            <div className="absolute bottom-0 inset-x-0 z-[60] max-h-[85vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="text-base font-semibold text-foreground">{sheetTitle}</div>
                <button
                  type="button"
                  onClick={() => setActiveSheet('main')}
                  className="rounded-full border border-border bg-ghost px-3 py-1 text-xs font-semibold text-foreground"
                >
                  {propertyStrings.mobileDone}
                </button>
              </div>
              <div className="mt-4 space-y-4">
                <AvailabilityCalendar
                  blocked={blockedDates}
                  minNights={property.minNights}
                  maxNights={property.maxNights}
                  minLeadDays={activeLeadDays}
                  minNightOverrides={minNightOverridesState}
                  showMinNightHint
                  showLegend={false}
                  monthsToShow={1}
                  layout="stacked"
                  onRangeSelected={({ checkIn: ci, checkOut: co }) => {
                    setCheckIn(ci);
                    setCheckOut(co);
                    setActiveSheet('main');
                  }}
                />
              </div>
            </div>
          )}

          {activeSheet === 'guests' && (
            <div className="absolute bottom-0 inset-x-0 z-[60] max-h-[85vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="text-base font-semibold text-foreground">{sheetTitle}</div>
                <button
                  type="button"
                  onClick={() => setActiveSheet('main')}
                  className="rounded-full border border-border bg-ghost px-3 py-1 text-xs font-semibold text-foreground"
                >
                  {propertyStrings.mobileDone}
                </button>
              </div>
              <div className="mt-4 space-y-4">
                <div className="space-y-2 rounded-2xl border border-border bg-card p-3">
                  {[
                    {
                      key: 'adults',
                      label: propertyStrings.guests.adults,
                      hint: propertyStrings.guests.adultsHint,
                      min: 1,
                    },
                    {
                      key: 'children',
                      label: propertyStrings.guests.children,
                      hint: propertyStrings.guests.childrenHint,
                      min: 0,
                    },
                    {
                      key: 'infants',
                      label: propertyStrings.guests.infants,
                      hint: propertyStrings.guests.infantsHint,
                      min: 0,
                    },
                    ...(property.maxPets && property.maxPets > 0
                      ? [{
                          key: 'pets',
                          label: propertyStrings.guests.pets,
                          hint: propertyStrings.guests.petsHint,
                          min: 0,
                        }]
                      : []),
                  ].map((row) => {
                    const value = guestCounts[row.key as keyof typeof guestCounts] as number;
                    const atMin = value <= row.min;
                    const atMax =
                      row.key === 'pets'
                        ? value >= (property.maxPets ?? 0)
                        : totalGuests >= property.maxGuests;

                    return (
                      <div key={row.key} className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-foreground">{row.label}</div>
                          <div className="text-xs text-muted-foreground">{row.hint}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateGuestCount(row.key as any, -1)}
                            disabled={atMin}
                            className="h-8 w-8 rounded-full border border-border bg-ghost text-foreground disabled:opacity-40"
                            aria-label={propertyStrings.guestControlDecrease(row.label)}
                          >
                            −
                          </button>
                          <div className="w-6 text-center text-sm font-semibold text-foreground">
                            {value}
                          </div>
                          <button
                            type="button"
                            onClick={() => updateGuestCount(row.key as any, 1)}
                            disabled={atMax}
                            className="h-8 w-8 rounded-full border border-border bg-ghost text-foreground disabled:opacity-40"
                            aria-label={propertyStrings.guestControlIncrease(row.label)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeSheet === 'rate' && (
            <div className="absolute bottom-0 inset-x-0 z-[60] max-h-[85vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="text-base font-semibold text-foreground">{sheetTitle}</div>
                <button
                  type="button"
                  onClick={() => setActiveSheet('main')}
                  className="rounded-full border border-border bg-ghost px-3 py-1 text-xs font-semibold text-foreground"
                >
                  {propertyStrings.mobileDone}
                </button>
              </div>
              <div className="mt-4 space-y-5">
                {ratePlans && ratePlans.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-foreground">{propertyStrings.ratePlanHeader}</div>
                    <div className="space-y-2">
                      {ratePlans.map((plan) => (
                        <label
                          key={plan.id}
                          className={`block w-full border rounded-2xl px-3 py-3 cursor-pointer transition-colors ${
                            selectedRatePlanId === plan.id
                              ? 'border-primary bg-accent/30'
                              : 'border-border'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{plan.name}</div>
                              {plan.description && (
                                <div className="text-xs text-muted-foreground">{plan.description}</div>
                              )}
                            </div>
                            <input
                              type="radio"
                              name="rate-plan-mobile"
                              value={plan.id}
                              checked={selectedRatePlanId === plan.id}
                              onChange={(e) => setSelectedRatePlanId(e.target.value)}
                              className="mt-1 shrink-0 text-primary focus:ring-primary"
                            />
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                            {plan.paymentRule.type === 'full-prepay' && (
                              <span className="px-2 py-1 bg-card rounded border border-border">
                                {propertyStrings.fullPrepay}
                              </span>
                            )}
                            {plan.paymentRule.type === 'deposit' && (
                              <span className="px-2 py-1 bg-card rounded border border-border">
                                {propertyStrings.depositLabel(
                                  Math.round(plan.paymentRule.depositPercent * 100),
                                  plan.paymentRule.remainderDaysBeforeCheckIn
                                )}
                              </span>
                            )}
                            {plan.paymentRule.type === 'pay-at-checkin' && (
                              <span className="px-2 py-1 bg-card rounded border border-border">
                                {propertyStrings.payAtCheckIn}
                              </span>
                            )}
                            {plan.refundLadder.length > 0 && (
                              <span className="px-2 py-1 bg-card rounded border border-border">
                                {propertyStrings.freeCancelUntilDays(plan.cancellationWindowDays)}
                              </span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {quote && (
                  <div className="rounded-2xl border border-border bg-accent/30 p-4 space-y-2">
                    <div className="text-sm font-semibold text-foreground">
                      {propertyStrings.priceBreakdownTitle}
                    </div>
                    <div className="space-y-2 text-sm text-foreground">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {propertyStrings.averageNightlyRate} {currencySymbol}{(
                            breakdownNightlyRate / 100
                          ).toFixed(2)} × {quote.pricing.nights} nights
                        </span>
                        <span className="text-foreground">
                          {currencySymbol}{((breakdownNightlyTotal ?? 0) / 100).toFixed(2)}
                        </span>
                      </div>
                      {hasDiscount && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{propertyStrings.discount}</span>
                          <span className="text-foreground">
                            -{currencySymbol}{(discountAmount / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {quote.pricing.cleaningFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{propertyStrings.cleaningFee}</span>
                          <span className="text-foreground">
                            {currencySymbol}{(quote.pricing.cleaningFee / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{taxRateLabel}</span>
                        <span className="text-foreground">
                          {currencySymbol}{(quote.pricing.taxes / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-border pt-2 flex justify-between font-bold">
                        <span className="text-foreground">{propertyStrings.total}</span>
                        <span className="text-foreground">
                          {currencySymbol}{(quote.pricing.total / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-border text-xs text-muted-foreground space-y-1">
                      <p className="font-semibold text-foreground mb-1">
                        {activePlan ? activePlan.name : propertyStrings.paymentCancellation}
                      </p>
                      <p>{paymentSummary}</p>
                      <p className="mt-1">{cancellationSummary}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

            {activeSheet === 'notice' && (
              <div className="absolute bottom-0 inset-x-0 z-[60] max-h-[85vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <div className="text-base font-semibold text-foreground">{strings.common.notice.title}</div>
                <button
                  type="button"
                  onClick={() => setActiveSheet('main')}
                  className="rounded-full border border-border bg-ghost px-3 py-1 text-xs font-semibold text-foreground"
                >
                  {propertyStrings.mobileDone}
                </button>
              </div>
              <div className="mt-4">
                <NoticeAtCollection />
              </div>
            </div>
            )}
        </div>
      )}
    </>
  );

  return <div className="lg:hidden">{portalTarget ? createPortal(content, portalTarget) : null}</div>;
}

function dateKeyLocal(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
